import React, { useEffect, useState } from "react";
import { MdImage } from "react-icons/md";
import Card from "components/card";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

const ProductSections = () => {
  const [sections, setSections] = useState([
    { id: 1, name: "", category: "", product_section_image: null },
    { id: 2, name: "", category: "", product_section_image: null },
    { id: 3, name: "", category: "", product_section_image: null },
  ]);

  const [categories, setCategories] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState({});

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Store the file temporarily
    setSelectedFiles((prev) => ({
      ...prev,
      [index]: file,
    }));

    // Create a temporary preview URL
    const previewUrl = URL.createObjectURL(file);
    const newSections = [...sections];
    newSections[index].product_section_image = previewUrl;
    setSections(newSections);
  };

  const handleNameChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].name = value;
    setSections(newSections);
  };

  const handleCategoryChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].category = value;
    setSections(newSections);
  };

  const fetchSections = async () => {
    try {
      const response = await adminApiService.getProductSections();
      if (!response.ok) throw new Error("Failed to fetch sections");
      const data = await response.json();
      setSections(data);
    } catch (error) {
      console.error("Error fetching sections:", error);
      toast.error("Failed to fetch sections");
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApiService.getAllCategories();
      if (!response.ok) throw new Error("Failed to fetch categories.");
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching categories.");
    }
  };

  const handleUpdateSection = async (sectionId) => {
    try {
      const sectionIndex = sections.findIndex(
        (section) => section.id === sectionId
      );
      if (sectionIndex === -1) return;

      const sectionToUpdate = sections[sectionIndex];
      const formData = new FormData();

      // Add section data
      formData.append("name", sectionToUpdate.name);
      formData.append("category", sectionToUpdate.category);

      // Add image if it was selected
      if (selectedFiles[sectionIndex]) {
        formData.append("product_section_image", selectedFiles[sectionIndex]);
      }

      const response = await adminApiService.updateProductSection(
        sectionId,
        formData
      );

      if (!response.ok) throw new Error("Failed to update section");

      const data = await response.json();

      // Update the section with the response data
      const newSections = [...sections];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        ...data,
        product_section_image: data.product_section_image,
      };
      setSections(newSections);

      // Clear the selected file for this section
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[sectionIndex];
        return newFiles;
      });

      toast.success("Section updated successfully!");
    } catch (error) {
      console.error("Error updating section:", error);
      toast.error("Failed to update section. Please try again.");
    }
  };

  useEffect(() => {
    fetchSections();
    fetchCategories();
  }, []);

  return (
    <div className="mb-8 mt-12 flex flex-col gap-12">
      <Card extra="!p-[20px]">
        <div className="mb-8 rounded-xl bg-gradient-to-r from-primary to-primaryDark p-6">
          <h6 className="text-xl font-bold text-white">
            Product Sections Settings
          </h6>
        </div>
        <div className="grid grid-cols-1 gap-6 p-6">
          {sections.map((section, index) => (
            <Card key={section.id} extra="p-6">
              <h6 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                Product Section {section.id}{" "}
                {section.id == 1 && "(Featured Section)"}
              </h6>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-white">
                    Section Name
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    placeholder="Enter section name"
                    value={section.name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                  />
                </div>
                {section.id != 1 && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-white">
                      Category
                    </label>
                    <select
                      className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 focus:border-primary focus:outline-none dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      value={section.category}
                      onChange={(e) =>
                        handleCategoryChange(index, e.target.value)
                      }
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-white">
                    Upload Image
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(index, e)}
                      className="hidden"
                      id={`image-upload-${section.id}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        document
                          .getElementById(`image-upload-${section.id}`)
                          .click()
                      }
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-700 hover:bg-gray-50 focus:outline-none dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                      <MdImage className="h-5 w-5" />
                      Upload Image
                    </button>
                    {section.product_section_image && (
                      <div className="relative">
                        <img
                          src={
                            section.product_section_image.startsWith("blob:")
                              ? section.product_section_image
                              : `${process.env.REACT_APP_API_URL_Plain}/config/uploads/product_section_image/${section.product_section_image}`
                          }
                          alt={`Section ${section.id}`}
                          className="h-50 w-80 rounded object-contain"
                        />
                        {section.product_section_image.startsWith("blob:") && (
                          <div className="text-black absolute right-2 top-2 rounded-full bg-yellow-400 px-2 py-1 text-xs">
                            Preview
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => handleUpdateSection(section.id)}
                    className="rounded-lg bg-primary px-6 py-2 text-white hover:bg-primaryDark focus:outline-none"
                  >
                    Update Section
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default ProductSections;
